import { enUS } from './en-US';
import type {
  MonkeysBuiltInLocaleId,
  MonkeysLocale,
  MonkeysLocaleInput,
  MonkeysLocaleMessages,
  MonkeysLocaleMessagesOverride,
  MonkeysResolvedLocale,
} from './types';
import { zhCN } from './zh-CN';

const BUILT_IN_LOCALES: Record<MonkeysBuiltInLocaleId, MonkeysLocale> = {
  'en-US': enUS,
  'zh-CN': zhCN,
};

function normalizeLocaleId(locale: unknown): string {
  if (typeof locale !== 'string' || !locale.trim()) {
    throw new TypeError('Monkeys locale must be a non-empty BCP 47 string.');
  }

  try {
    return Intl.getCanonicalLocales(locale)[0];
  } catch {
    throw new RangeError(
      `Monkeys locale "${locale}" is not a valid BCP 47 identifier.`,
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validateMessageOverride(
  value: unknown,
  schema: unknown,
  path: string,
) {
  if (!isRecord(value) || !isRecord(schema)) {
    throw new TypeError(`${path} must be an object.`);
  }

  for (const [key, candidate] of Object.entries(value)) {
    if (candidate === undefined) continue;
    if (!(key in schema)) {
      throw new TypeError(`${path}.${key} is not a supported Monkeys message.`);
    }

    const expected = schema[key];
    const candidatePath = `${path}.${key}`;
    if (typeof expected === 'function') {
      if (typeof candidate !== 'function') {
        throw new TypeError(`${candidatePath} must be a function.`);
      }
    } else if (typeof expected === 'string') {
      if (typeof candidate !== 'string') {
        throw new TypeError(`${candidatePath} must be a string.`);
      }
    } else {
      validateMessageOverride(candidate, expected, candidatePath);
    }
  }
}

function assertMessagesOverride(
  value: unknown,
  path: string,
): asserts value is MonkeysLocaleMessagesOverride {
  validateMessageOverride(value, enUS.messages, path);
}

function isBuiltInLocale(locale: string): locale is MonkeysBuiltInLocaleId {
  return locale === 'en-US' || locale === 'zh-CN';
}

function normalizeLocaleInput(input: MonkeysLocaleInput | undefined): {
  locale: string | undefined;
  messages: MonkeysLocaleMessagesOverride | undefined;
  hasExplicitLocale: boolean;
} {
  if (input === undefined) {
    return {
      locale: undefined,
      messages: undefined,
      hasExplicitLocale: false,
    };
  }

  if (typeof input === 'string') {
    const locale = normalizeLocaleId(input);
    if (!isBuiltInLocale(locale)) {
      throw new RangeError(
        `Unsupported built-in Monkeys locale "${locale}". Provide a locale pack object for custom locales.`,
      );
    }
    return { locale, messages: undefined, hasExplicitLocale: true };
  }

  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new TypeError('Monkeys locale must be a locale id or locale pack object.');
  }

  const locale = normalizeLocaleId(input.locale);
  assertMessagesOverride(input.messages, 'Monkeys locale messages');

  return {
    locale,
    messages: input.messages,
    hasExplicitLocale: true,
  };
}

export function mergeMonkeysLocaleMessages(
  base: MonkeysLocaleMessages,
  override?: MonkeysLocaleMessagesOverride,
): MonkeysLocaleMessages {
  if (!override) return base;

  return {
    pagination: { ...base.pagination, ...override.pagination },
    dialog: { ...base.dialog, ...override.dialog },
    dropdownMenu: { ...base.dropdownMenu, ...override.dropdownMenu },
    loading: { ...base.loading, ...override.loading },
    layout: { ...base.layout, ...override.layout },
    steps: { ...base.steps, ...override.steps },
    numberInput: { ...base.numberInput, ...override.numberInput },
    passwordInput: { ...base.passwordInput, ...override.passwordInput },
    multiSelect: { ...base.multiSelect, ...override.multiSelect },
    table: { ...base.table, ...override.table },
    toolbar: {
      theme: { ...base.toolbar.theme, ...override.toolbar?.theme },
      language: { ...base.toolbar.language, ...override.toolbar?.language },
    },
    toast: { ...base.toast, ...override.toast },
    dataExplorer: {
      viewTree: {
        ...base.dataExplorer.viewTree,
        ...override.dataExplorer?.viewTree,
      },
    },
    workbench: {
      collection: {
        ...base.workbench.collection,
        ...override.workbench?.collection,
      },
      interactiveTable: {
        ...base.workbench.interactiveTable,
        ...override.workbench?.interactiveTable,
      },
    },
    agentWorkbench: {
      ...base.agentWorkbench,
      ...override.agentWorkbench,
      modes: {
        ...base.agentWorkbench.modes,
        ...override.agentWorkbench?.modes,
      },
      composer: {
        ...base.agentWorkbench.composer,
        ...override.agentWorkbench?.composer,
      },
      quickStart: {
        ...base.agentWorkbench.quickStart,
        ...override.agentWorkbench?.quickStart,
      },
      tool: {
        ...base.agentWorkbench.tool,
        ...override.agentWorkbench?.tool,
        status: {
          ...base.agentWorkbench.tool.status,
          ...override.agentWorkbench?.tool?.status,
        },
      },
      approval: {
        ...base.agentWorkbench.approval,
        ...override.agentWorkbench?.approval,
      },
      artifact: {
        ...base.agentWorkbench.artifact,
        ...override.agentWorkbench?.artifact,
      },
      run: {
        ...base.agentWorkbench.run,
        ...override.agentWorkbench?.run,
      },
      process: {
        ...base.agentWorkbench.process,
        ...override.agentWorkbench?.process,
      },
      artifactWorkspace: {
        ...base.agentWorkbench.artifactWorkspace,
        ...override.agentWorkbench?.artifactWorkspace,
      },
      usage: {
        ...base.agentWorkbench.usage,
        ...override.agentWorkbench?.usage,
      },
      navigation: {
        ...base.agentWorkbench.navigation,
        ...override.agentWorkbench?.navigation,
      },
      details: {
        ...base.agentWorkbench.details,
        ...override.agentWorkbench?.details,
        tabs: {
          ...base.agentWorkbench.details.tabs,
          ...override.agentWorkbench?.details?.tabs,
        },
        status: {
          ...base.agentWorkbench.details.status,
          ...override.agentWorkbench?.details?.status,
        },
        state: {
          loading: {
            ...base.agentWorkbench.details.state.loading,
            ...override.agentWorkbench?.details?.state?.loading,
          },
          empty: {
            ...base.agentWorkbench.details.state.empty,
            ...override.agentWorkbench?.details?.state?.empty,
          },
          error: {
            ...base.agentWorkbench.details.state.error,
            ...override.agentWorkbench?.details?.state?.error,
          },
        },
        actions: {
          ...base.agentWorkbench.details.actions,
          ...override.agentWorkbench?.details?.actions,
        },
        workspace: {
          ...base.agentWorkbench.details.workspace,
          ...override.agentWorkbench?.details?.workspace,
          diffStatus: {
            ...base.agentWorkbench.details.workspace.diffStatus,
            ...override.agentWorkbench?.details?.workspace?.diffStatus,
          },
          fileOperation: {
            ...base.agentWorkbench.details.workspace.fileOperation,
            ...override.agentWorkbench?.details?.workspace?.fileOperation,
          },
        },
      },
    },
  };
}

export const defaultMonkeysLocale: MonkeysResolvedLocale = {
  locale: enUS.locale,
  messages: mergeMonkeysLocaleMessages(
    enUS.messages as MonkeysLocaleMessages,
  ),
};

export function resolveMonkeysLocale(
  input?: MonkeysLocaleInput,
  override?: MonkeysLocaleMessagesOverride,
  parent?: MonkeysResolvedLocale,
): MonkeysResolvedLocale {
  if (override !== undefined) {
    assertMessagesOverride(override, 'MonkeysProvider messages');
  }
  const normalized = normalizeLocaleInput(input);
  const locale = normalized.locale ?? parent?.locale ?? enUS.locale;
  const builtInMessages = isBuiltInLocale(locale)
    ? BUILT_IN_LOCALES[locale].messages
    : undefined;
  let messages = mergeMonkeysLocaleMessages(
    enUS.messages as MonkeysLocaleMessages,
    builtInMessages,
  );

  if (parent && (!normalized.hasExplicitLocale || parent.locale === locale)) {
    messages = mergeMonkeysLocaleMessages(messages, parent.messages);
  }

  messages = mergeMonkeysLocaleMessages(messages, normalized.messages);
  messages = mergeMonkeysLocaleMessages(messages, override);

  return { locale, messages };
}
